import { Treatment, BillingInfo, MedicalInsights, Orders, Subscription, Subscriptions, EnrichedTreatment } from '@dayetopia/library';
import { MedicalResults } from '@dayetopia/library/screening';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface MarketingPreferences {
  marketingEmail: boolean;
  newsletterSMS: boolean;
  marketingSMS: boolean;
}

interface UserInfoProps {
  accountId: string;
  firstName: string;
  lastName: string;
  pronouns: string;
  email: string;
  telephone: string;
  profileIcon: string;
  dateOfBirth: string;
  marketingPreferences: MarketingPreferences;
  credits?: any;
  termsAcceptedDate?: string;
  vmsEnabledDate?: string;
  ppcEnabledDate?: string;
  pprEnabledDate?: string;
  hasAcceptedDate?: boolean;
  referralCode?: string;
  personaVerificationId?: string;
  isMfaEnabled: boolean;
  mfaEnrolledFactor?: string[];
  emailVerified?: boolean;
}

export interface HealthProfileProps {
  results: MedicalResults;
  insights: MedicalInsights;
  treatments: Treatment[];
  enrichedTreatments: EnrichedTreatment[];
}

interface PurchaseProps {
  orders?: Orders;
  subscriptions?: Subscriptions;
  billingInfo?: BillingInfo;
  selectedSubscriptionId: string;
  fetchedAccountData: boolean;
  selectedSub?: Subscription;
}

export enum AccountIcons {
  Initials = 'Initials',
  MediumHair = 'MediumHair',
  LongHair = 'LongHair',
  Bun = 'Bun',
  Afro = 'Afro',
  ShortHair = 'ShortHair'
}

export const userInfo: UserInfoProps = {
  accountId: '',
  firstName: '',
  lastName: '',
  pronouns: '',
  email: '',
  telephone: '',
  dateOfBirth: '',
  profileIcon: AccountIcons.Initials,
  marketingPreferences: {
    marketingEmail: false,
    newsletterSMS: false,
    marketingSMS: false
  },
  credits: [],
  termsAcceptedDate: undefined,
  vmsEnabledDate: undefined,
  ppcEnabledDate: undefined,
  pprEnabledDate: undefined,
  personaVerificationId: undefined,
  isMfaEnabled: false,
  emailVerified: false
};

const healthProfile: HealthProfileProps = {
  insights: [],
  results: [],
  treatments: [],
  enrichedTreatments: []
};

const purchases: PurchaseProps = {
  orders: [],
  subscriptions: [],
  billingInfo: undefined,
  selectedSubscriptionId: '',
  fetchedAccountData: false
};

export interface UseAccountStoreProps {
  userInfo: UserInfoProps;
  purchases: PurchaseProps;
  healthProfile: HealthProfileProps;
  loading: boolean;
  healthProfileLoaded: boolean;
  manuallyUpdatedFields: string[];
  setUserInfo: (value: UserInfoProps) => void;
  setPurchases: (value: PurchaseProps) => void;
  setHealthProfile: (value: HealthProfileProps) => void;
  setLoading: (value: boolean) => void;
  setHealthProfileLoaded: (value: boolean) => void;
  addManuallyUpdatedFields: (fields: string[]) => void;
  removeManuallyUpdatedFields: (fields: string[]) => void;
  clearManuallyUpdatedFields: () => void;
  reset: () => void;
}

export const useAccountStore = create(
  persist<UseAccountStoreProps>(
    (set) => ({
      userInfo,
      setUserInfo: (newValue: UserInfoProps) => set({ userInfo: newValue }),
      purchases,
      setPurchases: (newValue: PurchaseProps) => set({ purchases: newValue }),
      healthProfile,
      setHealthProfile: (newValue: HealthProfileProps) => set({ healthProfile: newValue }),
      healthProfileLoaded: false,
      loading: true,
      manuallyUpdatedFields: [],
      setLoading: (val: boolean) => set({ loading: val }),
      setHealthProfileLoaded: (val: boolean) => set({ healthProfileLoaded: val }),
      addManuallyUpdatedFields: (fields: string[]) =>
        set((state) => {
          // Handle both old Set format and new array format from persisted storage
          const currentFields = Array.isArray(state.manuallyUpdatedFields) ? state.manuallyUpdatedFields : [];
          return {
            manuallyUpdatedFields: Array.from(new Set([...currentFields, ...fields]))
          };
        }),
      removeManuallyUpdatedFields: (fields: string[]) =>
        set((state) => {
          const currentFields = Array.isArray(state.manuallyUpdatedFields) ? state.manuallyUpdatedFields : [];
          return {
            manuallyUpdatedFields: currentFields.filter((field) => !fields.includes(field))
          };
        }),
      clearManuallyUpdatedFields: () => set({ manuallyUpdatedFields: [] }),
      reset: () => set({ userInfo, purchases, healthProfile, healthProfileLoaded: false, manuallyUpdatedFields: [], loading: true })
    }),
    {
      name: 'vms-state',
      version: 1,
      storage: {
        getItem: (name) => {
          const value = localStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch {
            // QuotaExceededError — localStorage full, silently skip persisting
          }
        },
        removeItem: (name) => localStorage.removeItem(name)
      },
      migrate: (persistedState: any) => {
        // Clean up old Set format if it exists - return new object instead of mutating
        if (persistedState?.manuallyUpdatedFields && !Array.isArray(persistedState.manuallyUpdatedFields)) {
          return {
            ...persistedState,
            manuallyUpdatedFields: []
          };
        }
        return persistedState;
      }
    }
  )
);
